import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getCategories } from '../services/categoryService';
import { getProductById } from '../services/productService';
import { getPageBySlug } from '../services/pageService';
import { getPostById } from '../services/blogService';

function niceLabel(segment) {
    if (!segment) return '';
    return segment.replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function normalizeStr(s) {
    if (s === undefined || s === null) return '';
    try {
        // trim, unicode-normalize and remove diacritics, then lowercase
        return String(s).trim().normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLowerCase();
    } catch (err) {
        // fallback for older runtimes: basic remove combining marks range
        return String(s).trim().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }
}

export default function Breadcrumbs({ separator = '›', hideOnRoot = true }) {
    const loc = useLocation();
    const [crumbs, setCrumbs] = useState([]);
    // Debug UI removed: breadcrumb debug toggle and debug panel are disabled

    useEffect(() => {
        let mounted = true;
        const build = async () => {
            const parts = loc.pathname.split('/').filter(Boolean);
            const params = new URLSearchParams(loc.search || '');
            const categoryParam = params.get('category');
            const categoryIdParam = params.get('categoryId');
            const base = [{ to: '/', label: 'Kreu' }];

            const isProductView = parts[0] === 'products' && parts[1];
            // If a `categoryId` or `category` query param exists (and we're not on a product detail),
            // treat the page as a category view so breadcrumbs reflect the selected category.
            const isCategoryView = (Boolean(categoryParam) || Boolean(categoryIdParam)) && !isProductView;

            // Helper to build a standardized category path from either an array or a string.
            const resolveProductCategoryPath = async (p) => {
                if (!p) return null;

                // NEW: Use categoryId if available (Relational primary)
                if (p.categoryId) {
                    try {
                        const tree = await getCategories();
                        const { getCategoryPathById } = await import('../services/categoryService');
                        const path = await getCategoryPathById(p.categoryId, tree);
                        if (path && path.length) return path.map(c => c.name);
                    } catch (e) { }
                }

                // Fallback: If explicit categories array present
                if (Array.isArray(p.categories) && p.categories.length) return p.categories.map(c => typeof c === 'string' ? c : (c && c.name) || String(c));

                // Fallback: Legacy category string
                if (p.category) {
                    try {
                        const tree = await getCategories();
                        const decoded = String(p.category || '').trim();
                        const partsFromStr = String(decoded).split(new RegExp('\\s*[›>\\/\\\\-]\\s*')).map(s => s.trim()).filter(Boolean);
                        const targetCandidates = partsFromStr.length ? partsFromStr.slice().reverse() : [decoded];
                        for (const cand of targetCandidates) {
                            const targetNorm = normalizeStr(cand || decoded);
                            function findPath(nodes, target, path = []) {
                                for (const n of nodes || []) {
                                    const nextPath = path.concat(n.name);
                                    if (normalizeStr(n.name) === target) return nextPath;
                                    if (Array.isArray(n.subcategories) && n.subcategories.length) {
                                        const found = findPath(n.subcategories, target, nextPath);
                                        if (found) return found;
                                    }
                                }
                                return null;
                            }
                            const pathArr = findPath(Array.isArray(tree) ? tree : [], targetNorm);
                            if (pathArr && pathArr.length) return pathArr;
                        }
                    } catch (e) { }
                }
                return null;
            };

            // Build crumbs for product view
            if (isProductView) {
                try {
                    const p = await getProductById(parts[1]);
                    if (p) console.debug('Breadcrumbs: product payload', { id: parts[1], product: p });
                    const out = [];
                    const seen = new Set();
                    const add = (to, label) => {
                        const key = normalizeStr(label || '');
                        if (seen.has(key)) return;
                        seen.add(key);
                        out.push({ to, label });
                    };

                    add('/products', 'Products');

                    let productCatPath = await resolveProductCategoryPath(p);
                    if (productCatPath) console.debug('Breadcrumbs: resolved productCatPath', productCatPath);

                    if (!productCatPath && p && p.category) {
                        const naive = String(p.category).split(new RegExp('\\s*[›>\\/\\\\-]\\s*')).map(s => s.trim()).filter(Boolean);
                        if (naive.length) productCatPath = naive;
                    }

                    if (Array.isArray(productCatPath) && productCatPath.length) {
                        const mapped = productCatPath.map(catLabel => ({ to: { pathname: '/products', search: `?category=${encodeURIComponent(catLabel)}` }, label: catLabel }));
                        const idx = out.findIndex(x => normalizeStr(x.label) === normalizeStr('products'));
                        if (idx !== -1) out.splice(idx, 1);
                        for (const m of mapped) add(m.to, m.label);
                    }

                    if (p && p.name) add(`${loc.pathname}`, p.name);
                    else add(`${loc.pathname}`, niceLabel(parts[1]));

                    if (mounted) setCrumbs(base.concat(out));
                    return;
                } catch (err) {
                    console.error('Breadcrumbs: failed to resolve product', err);
                }
            }

            // Category view: build full category path
            if (isCategoryView) {
                try {
                    const tree = await getCategories();
                    const { getCategoryPathById } = await import('../services/categoryService');

                    let pathArr = null;

                    // A: Resolve by categoryId (Preferred)
                    if (categoryIdParam) {
                        const path = await getCategoryPathById(categoryIdParam, tree);
                        if (path && path.length) pathArr = path.map(c => c.name);
                    }

                    // B: Fallback to category string
                    if (!pathArr && categoryParam) {
                        const decodedTarget = decodeURIComponent(categoryParam || '');
                        const partsFromParam = String(decodedTarget).split(new RegExp('\\s*[›>\\/\\\\-]\\s*')).map(s => s.trim()).filter(Boolean);
                        const targetCandidates = partsFromParam.length ? partsFromParam.slice().reverse() : [decodedTarget];

                        function findPathRecursive(nodes, targetNorm, path = []) {
                            for (const n of nodes || []) {
                                const nextPath = path.concat(n.name);
                                const nameNorm = normalizeStr(n.name || '');
                                if (nameNorm === targetNorm || nameNorm.includes(targetNorm) || targetNorm.includes(nameNorm)) return nextPath;
                                if (Array.isArray(n.subcategories) && n.subcategories.length) {
                                    const found = findPathRecursive(n.subcategories, targetNorm, nextPath);
                                    if (found) return found;
                                }
                            }
                            return null;
                        }

                        for (const cand of targetCandidates) {
                            const candNorm = normalizeStr(cand || decodedTarget);
                            pathArr = findPathRecursive(Array.isArray(tree) ? tree : [], candNorm);
                            if (pathArr && pathArr.length) break;
                        }
                    }

                    const out = [];
                    const seen = new Set();
                    const add = (to, label) => { if (!seen.has(normalizeStr(label || ''))) { seen.add(normalizeStr(label || '')); out.push({ to, label }); } };

                    if (pathArr && pathArr.length) {
                        for (const catLabel of pathArr) add({ pathname: '/products', search: `?category=${encodeURIComponent(catLabel)}` }, catLabel);
                    } else if (categoryParam) {
                        add({ pathname: '/products', search: `?category=${encodeURIComponent(categoryParam)}` }, categoryParam);
                    }
                    if (mounted) setCrumbs(base.concat(out));
                    return;
                } catch (err) {
                    console.error('Breadcrumbs: failed to resolve category path', err);
                }
            }

            // Default behaviour: simple path segments with attempts to resolve pages/blogs
            try {
                const out = [];
                const seen = new Set();
                const add = (to, label) => { if (!seen.has(normalizeStr(label || ''))) { seen.add(normalizeStr(label || '')); out.push({ to, label }); } };
                let acc = '';
                for (let i = 0; i < parts.length; i++) {
                    const seg = parts[i];
                    acc += `/${seg}`;
                    const prev = parts[i - 1];
                    let label = niceLabel(seg);
                    try {
                        const isPage = prev === 'pages' || parts[0] === 'pages' || (parts.length === 1 && i === 0);
                        const builtInSlugs = ['admin', 'api', 'cart', 'checkout', 'login', 'register', 'profile', 'products', 'blogs', 'about', 'contact', 'services'];
                        if (isPage && !builtInSlugs.includes(normalizeStr(seg))) {
                            const pg = await getPageBySlug(seg);
                            if (pg && pg.title) label = pg.title;
                        } else if (prev === 'blogs' || parts[0] === 'blogs') {
                            const blog = await getPostById(seg);
                            if (blog && blog.title) label = blog.title;
                        }
                    } catch (e) { /* ignore */ }
                    add(acc, label);
                }
                if (mounted) setCrumbs(base.concat(out));
                return;
            } catch (err) {
                console.error('Breadcrumbs: default build failed', err);
            }
        };

        build();
        return () => { mounted = false; };
    }, [loc.pathname, loc.search]);

    // If only home and hideOnRoot requested, don't render nav
    if (hideOnRoot && crumbs.length <= 1) return null;

    return (
        <div className="breadcrumb-wrapper" style={{ width: '100%', padding: '2px 0' }}>
            <style>{`
                .glb-breadcrumb-link {
                    color: #007da5;
                    text-decoration: none;
                    font-size: 13.5px;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    transition: color 0.2s ease;
                }
                .glb-breadcrumb-link:hover {
                    color: #f7931e;
                }
                .glb-breadcrumb-current {
                    color: #094859;
                    font-weight: 700;
                    font-size: 13.5px;
                    display: inline-flex;
                    align-items: center;
                }
                .glb-breadcrumb-chevron {
                    color: #94a3b8;
                    display: inline-flex;
                    align-items: center;
                    flex-shrink: 0;
                }
            `}</style>
            <nav aria-label="Breadcrumb" style={{ fontSize: 13.5, color: '#333' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                        {crumbs.map((c, i) => (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                {(() => {
                                    const isHome = i === 0;
                                    const HomeIcon = (
                                        <svg
                                            aria-hidden="true"
                                            width="15"
                                            height="15"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            style={{ display: 'inline-block', verticalAlign: '-1px', marginRight: 5, flexShrink: 0 }}
                                        >
                                            <path d="M3 9.5L12 2.5l9 7v11a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 20.5v-11z" />
                                            <path d="M9 22V12h6v10" />
                                        </svg>
                                    );
                                    if (i < crumbs.length - 1) {
                                        return (
                                            <Link to={c.to} className="glb-breadcrumb-link">
                                                {isHome ? HomeIcon : null}
                                                <span>{c.label}</span>
                                            </Link>
                                        );
                                    }
                                    return (
                                        <span aria-current="page" className="glb-breadcrumb-current">
                                            {isHome ? HomeIcon : null}
                                            <span>{c.label}</span>
                                        </span>
                                    );
                                })()}
                                {i < crumbs.length - 1 ? (
                                    <span className="glb-breadcrumb-chevron">
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </span>
                                ) : null}
                            </li>
                        ))}
                    </ol>
                    <div style={{ marginLeft: 12 }} />
                </div>
            </nav>
        </div>
    );
}
